import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, KeyRound, Shield, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { api, fmtDate, apiError } from "../lib/api";
import { ROLE_LABELS, ROLE_OPTIONS, ROLE_PRESETS, MODULES, ACTIONS, MODULE_LABELS, ACTION_LABELS, effectivePerms } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [pwOpen, setPwOpen] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [forceChange, setForceChange] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permGrid, setPermGrid] = useState({});

  const load = async () => {
    setLoading(true);
    try { setUsers((await api.get("/users")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);

  const openPerms = (u) => {
    const eff = effectivePerms(u);
    const base = eff === null ? Object.fromEntries(MODULES.map((m) => [m, [...ACTIONS]])) : eff;
    setPermGrid(Object.fromEntries(MODULES.map((m) => [m, [...(base[m] || [])]])));
    setPermUser(u);
  };
  const togglePerm = (m, a) => setPermGrid((g) => {
    const cur = new Set(g[m] || []);
    cur.has(a) ? cur.delete(a) : cur.add(a);
    return { ...g, [m]: [...cur] };
  });
  const selectAllPerms = () => setPermGrid(Object.fromEntries(MODULES.map((m) => [m, [...ACTIONS]])));
  const clearAllPerms = () => setPermGrid(Object.fromEntries(MODULES.map((m) => [m, []])));
  const resetToPreset = () => {
    const preset = ROLE_PRESETS[permUser.role] || {};
    setPermGrid(Object.fromEntries(MODULES.map((m) => [m, [...(preset[m] || [])]])));
  };
  const savePerms = async () => {
    const clean = Object.fromEntries(Object.entries(permGrid).filter(([, a]) => a.length));
    try { await api.put(`/users/${permUser.id}/permissions`, { permissions: clean }); toast.success("Permissions saved"); setPermUser(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditing(null); setForm({ role: "sales", active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Employee name is required"); return; }
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          name: form.name, employee_name: form.employee_name || form.name, email: form.email,
          username: form.username, role: form.role, department: form.department, active: form.active,
          employee_id: form.employee_id || null,
        });
      } else {
        if (!form.email || !form.password) { toast.error("Email and password are required"); return; }
        await api.post("/users", form);
      }
      toast.success(`User ${editing ? "updated" : "created"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetPw = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.post(`/users/${pwOpen.id}/reset-password`, { password: newPw, force_change: forceChange }); toast.success("Password reset"); setPwOpen(null); setNewPw(""); setForceChange(false); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter((u) => ["name", "email", "username", "role", "department"].some((k) => String(u[k] || "").toLowerCase().includes(q)));
  }, [users, query]);

  return (
    <div data-testid="module-users">
      <div className="mb-5 flex items-center gap-2"><Shield className="w-6 h-6 text-[#0B132B]" /><div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">User Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">Admin only · create users, assign roles, reset passwords</p></div></div>

      <a href="/request-permissions" data-testid="goto-request-permissions" className="flex items-center justify-between gap-3 bg-[#0B132B] text-white rounded-sm px-4 py-3 mb-5 hover:bg-[#1C2541] transition-colors">
        <div className="flex items-center gap-3"><ShieldCheck className="w-5 h-5" /><div>
          <div className="font-semibold text-sm">Request Permission Matrix</div>
          <div className="text-xs text-slate-300">Control who can send which request type to which department — role and user level</div></div></div>
        <span className="text-xs bg-white/15 rounded-sm px-2.5 py-1">Open →</span>
      </a>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500">{filtered.length} user{filtered.length !== 1 ? "s" : ""}</p>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-users" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-full sm:w-52 bg-white" /></div>
          <Button data-testid="add-users-btn" onClick={openCreate} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Add User</span></Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            {["Employee", "Username / Email", "Role", "Department", "Status", "Last Login", "Actions"].map((h, i) => (
              <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No users</td></tr>
              : filtered.map((u) => (
                <tr key={u.id} data-testid={`row-users-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{u.employee_name || u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.username || u.email}<div className="text-xs text-slate-400">{u.email}</div></td>
                  <td className="px-4 py-3"><Badge variant="outline" className="rounded-sm bg-blue-50 text-blue-700 border-blue-200">{ROLE_LABELS[u.role] || u.role}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{u.department || "-"}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Switch data-testid={`active-toggle-${u.id}`} checked={u.active !== false} onCheckedChange={() => toggleActive(u)} /><span className={`text-xs ${u.active !== false ? "text-emerald-600" : "text-red-600"}`}>{u.active !== false ? "Active" : "Inactive"}</span></div></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{u.last_login ? fmtDate(u.last_login) : "Never"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`edit-user-${u.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" onClick={() => openEdit(u)}><Pencil className="w-4 h-4" /></Button>
                      <Button data-testid={`perms-user-${u.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-indigo-600" title="Permissions" onClick={() => openPerms(u)}><SlidersHorizontal className="w-4 h-4" /></Button>
                      <Button data-testid={`reset-pw-${u.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-amber-600" title="Reset password" onClick={() => { setPwOpen(u); setNewPw(""); }}><KeyRound className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "New"} User</DialogTitle><DialogDescription className="sr-only">User form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1 sm:col-span-2"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Employee Name *</label>
              <Input data-testid="field-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Username</label>
              <Input data-testid="field-username" value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email {editing ? "" : "*"}</label>
              <Input data-testid="field-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white" /></div>
            {!editing && <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password *</label>
              <Input data-testid="field-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white" /></div>}
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role *</label>
              <Select value={form.role || "sales"} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="field-role" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Linked Employee (for My HR self service)</label>
              <Select value={form.employee_id || "none"} onValueChange={(v) => setForm({ ...form, employee_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="field-employee-link" className="bg-white"><SelectValue placeholder="Not linked" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.employee_code ? ` (${e.employee_code})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">Leave blank to auto-match by email or name.</p></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Department</label>
              <Input data-testid="field-department" value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1 flex items-center gap-2 pt-5"><Switch data-testid="field-active" checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="text-sm text-slate-600">Active</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-user-btn" onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Reset Password</DialogTitle><DialogDescription>Set a new password for {pwOpen?.name}.</DialogDescription></DialogHeader>
          <Input data-testid="reset-pw-input" type="password" placeholder="New password (min 6)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-white" />
          <label className="flex items-center gap-2 text-sm text-slate-600 mt-2 cursor-pointer">
            <input data-testid="reset-pw-force" type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} className="rounded border-slate-300" />
            Force password change on next login (optional)
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="reset-pw-confirm" onClick={resetPw} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!permUser} onOpenChange={(o) => !o && setPermUser(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Permissions · {permUser?.name}</DialogTitle>
            <DialogDescription>Role preset: {ROLE_LABELS[permUser?.role] || permUser?.role}. Custom toggles override the preset.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-2 mb-2">
            <Button data-testid="perm-select-all" size="sm" variant="outline" className="rounded-sm" onClick={selectAllPerms}>Select All</Button>
            <Button data-testid="perm-clear-all" size="sm" variant="outline" className="rounded-sm" onClick={clearAllPerms}>Clear All</Button>
            <Button data-testid="perm-reset" size="sm" variant="outline" className="rounded-sm" onClick={resetToPreset}>Reset to Role Preset</Button>
          </div>
          <div className="border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-left text-slate-500">
                <th className="px-3 py-2 min-w-[150px]">Module</th>
                {ACTIONS.map((a) => <th key={a} className="px-2 py-2 text-center">{ACTION_LABELS[a]}</th>)}
              </tr></thead>
              <tbody>
                {MODULES.map((m) => (
                  <tr key={m} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-700">{MODULE_LABELS[m]}</td>
                    {ACTIONS.map((a) => (
                      <td key={a} className="px-2 py-2 text-center">
                        <input data-testid={`perm-${m}-${a}`} type="checkbox" className="w-4 h-4 accent-[#0B132B] cursor-pointer"
                          checked={!!(permGrid[m] && permGrid[m].includes(a))} onChange={() => togglePerm(m, a)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermUser(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="perm-save" onClick={savePerms} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
